import Button from "app/storybookComponents/Button";
import { forwardRef, useState } from "react";
import { Card } from "react-bootstrap";
import { ConflictArea } from "./types";
import SectionHeader from "./SectionHeader";
import AvatarCircle from "app/components/AvatarCircle";
import useCollapsibleSection from "./useCollapsibleSection";

interface Props {
  readonly conflictAreas: ConflictArea[];
  readonly forceExpanded?: boolean;
  // When conflictAreas is a partial page/chunk (PDF export), this is the
  // true count across all chunks, so the summary line stays accurate.
  readonly totalConflictCount?: number;
}

const ConflictAreaContent = ({
  conflictArea,
  forPdf,
}: {
  conflictArea: ConflictArea;
  forPdf?: boolean;
}) => (
  <div>
    <div className="card-with-left-strip column-gap-8px dark-blue card workplace-insights-simple-card-with-side-strip conflicting-area">
      <div style={{ marginLeft: "10px" }}>
        <div>
          <h3>{conflictArea.conflictArea.trait}</h3>
          <p>{conflictArea.conflictArea.description}</p>
        </div>
        <div style={{ display: "flex" }}>
          <AvatarCircle
            userAccountId={conflictArea.userAccountId}
            forPdf={forPdf}
          />
          <AvatarCircle
            userAccountId={conflictArea.userAccountId2}
            forPdf={forPdf}
          />
        </div>
      </div>
    </div>
    <div className="responsive-two-column-container mt-3">
      <Card className="workplace-insights-simple-card">
        <h3>Reflection Questions</h3>
        {conflictArea.conflictArea.reflectionQuestions.map(
          (question, index) => (
            <p key={question}>
              {index + 1}. {question}
            </p>
          )
        )}
      </Card>
      <Card className="workplace-insights-simple-card">
        <h3>Action Items</h3>
        {conflictArea.conflictArea.actionItems.map((actionItem, index) => (
          <p key={actionItem}>
            {index + 1}. {actionItem}
          </p>
        ))}
      </Card>
    </div>
  </div>
);

const TeamPotentialConflictAreas = forwardRef<HTMLDivElement, Props>(
  function TeamPotentialConflictAreas(
    { conflictAreas, forceExpanded, totalConflictCount },
    ref
  ) {
    const [conflictAreaIndex, setConflictAreaIndex] = useState(0);
    const { collapsed, toggleIsCollapsed } = useCollapsibleSection(
      forceExpanded,
      true
    );

    const setNewConflictAreaIndex = () => {
      const newIndex = (conflictAreaIndex + 1) % conflictAreas.length;
      setConflictAreaIndex(newIndex);
    };

    if (!conflictAreas.length) {
      return null;
    }

    const numberOfPotentialConflicts =
      totalConflictCount ?? conflictAreas.length;
    const areaOrAreas = numberOfPotentialConflicts === 1 ? "area" : "areas";
    if (collapsed) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="Learning Opportunities"
            isCollapsed={collapsed}
          />
        </Card>
      );
    }

    return (
      <Card
        ref={ref}
        style={{
          minHeight: "410px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div className="column-gap-20px">
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="Learning Opportunities"
            isCollapsed={collapsed}
            hideActionButton={forceExpanded}
          />
          <p>
            These are combinations of teammates with wide differences in
            personality traits and how that may impact your team. Your team has{" "}
            {numberOfPotentialConflicts} learning opportunities {areaOrAreas}.
          </p>
          {forceExpanded ? (
            conflictAreas.map((conflictArea) => (
              <ConflictAreaContent
                key={`${conflictArea.userAccountId}-${conflictArea.userAccountId2}`}
                conflictArea={conflictArea}
                forPdf={forceExpanded}
              />
            ))
          ) : (
            <ConflictAreaContent
              conflictArea={conflictAreas[conflictAreaIndex]}
              forPdf={forceExpanded}
            />
          )}
          {forceExpanded ? null : (
            <Button
              onClick={setNewConflictAreaIndex}
              style={{ alignSelf: "flex-start" }}
              disabled={conflictAreas.length === 1}
              variant="secondary-blue"
            >
              See another learning opportunity
            </Button>
          )}
        </div>
      </Card>
    );
  }
);

export default TeamPotentialConflictAreas;
